export interface Bot {
  id: string;
  businessName: string;
  phoneNumberId?: string;
  accessToken?: string;
  welcomeMessage: string;
  status: string;
  createdAt: number;
  botType?: string;
  aiChannel?: string;
  fbMode?: string;
  phoneNumber?: string;
  fbPageId?: string;
  websiteUrl?: string;
  allowedWebsites?: string[];
  contactEmail?: string;
  industry?: string;
}

export interface Analytics {
  total: {
    totalMessages: number;
    totalUsers: number;
    totalInteractions: number;
  };
  daily: Array<{ date: string; messages: number; users: number }>;
  recentConversations: Array<{
    message: string;
    response: string;
    timestamp: number;
    userId: string;
    type: string;
  }>;
}

export const BOT_ICONS: Record<string, string> = {
  whatsapp: '💬', facebook: '📘', website: '🌐', voice: '🎙️', ai: '🤖', email: '📧',
};
export const BOT_LABELS: Record<string, string> = {
  whatsapp: 'WhatsApp', facebook: 'Facebook', website: 'Website', voice: 'Voice', ai: 'AI Bot', email: 'Email',
};
export const AI_CHANNEL_ICONS: Record<string, string> = { whatsapp: '💬', website: '🌐', facebook: '📘' };
export const AI_CHANNEL_LABELS: Record<string, string> = { whatsapp: 'AI (WhatsApp)', website: 'AI (Website)', facebook: 'AI (Facebook)' };
export const TONE_OPTIONS = [
  { value: 'friendly', label: 'Friendly & Casual', hint: 'Warm, conversational — like chatting with a helpful friend.' },
  { value: 'marketer', label: 'Marketer', hint: 'Sells the value and benefit, ends replies with a relevant follow-up.' },
  { value: 'sales',    label: 'Sales-driven', hint: 'Always pushes toward a next step — quote, booking, or contact details.' },
  { value: 'straight', label: 'Straight to the Point', hint: 'Brief and direct, no pitch, no fluff.' },
];

export function botIcon(bot: Bot) {
  if (bot.botType === 'ai') return AI_CHANNEL_ICONS[bot.aiChannel || 'website'] || '🤖';
  if (bot.botType === 'facebook' && bot.fbMode === 'comments') return '📝';
  return BOT_ICONS[bot.botType || 'whatsapp'] || '🤖';
}
export function botLabel(bot: Bot) {
  if (bot.botType === 'ai') return AI_CHANNEL_LABELS[bot.aiChannel || 'website'] || 'AI Bot';
  if (bot.botType === 'facebook' && bot.fbMode === 'comments') return 'Facebook Comments';
  return BOT_LABELS[bot.botType || 'whatsapp'] || 'Bot';
}

export function statusColor(status: string) {
  if (status === 'active') return '#10b981';
  if (status === 'pending_verification' || status === 'pending_setup') return '#f59e0b';
  if (status === 'suspended') return '#ef4444';
  if (status === 'pending_payment') return '#f59e0b';
  if (status === 'trial_expired') return '#f97316';
  return '#64748b';
}
export function statusLabel(status: string) {
  if (status === 'active') return 'Live';
  if (status === 'pending_verification') return 'Pending';
  if (status === 'pending_setup') return 'Setup';
  if (status === 'suspended') return 'Suspended';
  if (status === 'pending_payment') return 'Unpaid';
  if (status === 'trial_expired') return 'Trial Ended';
  return status;
}

export function generateWebhookSecret(botId: string, businessName: string) {
  const str = `${botId}-${businessName.trim()}-autopilot`;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash;
  }
  return Math.abs(hash).toString(16).substring(0, 8);
}

// Format WhatsApp phone: 2637XXXXXXXX → +263 77 XXX XXXX
export function formatPhone(userId: string): string {
  const digits = userId.replace(/\D/g, '');
  if (digits.startsWith('263') && digits.length === 12) {
    const local = digits.slice(3);
    return `+263 ${local.slice(0, 2)} ${local.slice(2, 5)} ${local.slice(5)}`;
  }
  if (digits.startsWith('27') && digits.length === 11) {
    const local = digits.slice(2);
    return `+27 ${local.slice(0, 2)} ${local.slice(2, 5)} ${local.slice(5)}`;
  }
  if (digits.length >= 10) return `+${digits}`;
  return userId;
}

export const companyName = process.env.REACT_APP_COMPANY_NAME || 'autopilot';
export const widgetURL = process.env.REACT_APP_WIDGET_URL || 'https://zimchat.onrender.com';
export const API_BASE = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');
